package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Attachment
import com.tedd.db.Skill_resource
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.crypto.Pbkdf2
import org.teddapp.project.db.nowMillis
import org.teddapp.project.resources.PickedFile
import org.teddapp.project.resources.ResourceRules
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Les ressources d'une compétence : liens, textes saisis et fichiers, que l'assistant lit.
 * Les contrôles du serveur sont refaits ici avant d'écrire : un refus se dit tout de
 * suite, au lieu d'arriver au push suivant.
 */
class SkillResourceRepository(private val db: TeddDatabase) {

    /** L'issue d'un ajout : la ressource créée, ou ce qui l'empêche. */
    sealed interface Added {
        data class Done(val id: String) : Added
        data class Problem(val message: String) : Added
    }

    fun observeBySkill(skillId: String): Flow<List<Skill_resource>> =
        db.skillResourceQueries.selectBySkill(skillId).asFlow().mapToList(Dispatchers.Default)

    /** Supprimées depuis moins de 30 jours : on peut encore les restaurer. */
    fun observeTrash(skillId: String, now: Long = nowMillis()): Flow<List<Skill_resource>> =
        db.skillResourceQueries.selectTrashBySkill(skillId, now - TRASH_MS).asFlow().mapToList(Dispatchers.Default)

    /** Les fichiers des ressources, pour savoir lesquels sont encore à envoyer. */
    fun observeFiles(): Flow<List<Attachment>> =
        db.attachmentQueries.selectByOwnerKind(OWNER).asFlow().mapToList(Dispatchers.Default)

    suspend fun countLive(skillId: String): Int = withContext(Dispatchers.Default) {
        db.skillResourceQueries.countLive(skillId).executeAsOne().toInt()
    }

    suspend fun addLink(skillId: String, url: String, title: String): Added = withContext(Dispatchers.Default) {
        val clean = url.trim()
        ResourceRules.urlProblem(clean)?.let { return@withContext Added.Problem(it) }
        ResourceRules.titleProblem(title)?.let { return@withContext Added.Problem(it) }
        quotaProblem(skillId)?.let { return@withContext Added.Problem(it) }
        val key = ResourceRules.urlKey(clean)
        val taken = db.skillResourceQueries.selectLinks(skillId).executeAsList().any { ResourceRules.urlKey(it.url.orEmpty()) == key }
        if (taken) return@withContext Added.Problem("Ce lien est déjà dans la compétence.")
        Added.Done(insert(skillId, "link", "html", title.trim(), clean, null))
    }

    suspend fun addText(skillId: String, title: String, body: String, markdown: Boolean): Added = withContext(Dispatchers.Default) {
        ResourceRules.bodyProblem(body)?.let { return@withContext Added.Problem(it) }
        ResourceRules.titleProblem(title)?.let { return@withContext Added.Problem(it) }
        quotaProblem(skillId)?.let { return@withContext Added.Problem(it) }
        Added.Done(insert(skillId, "text", if (markdown) "markdown" else "text", title.trim(), null, body))
    }

    /**
     * Un fichier : la ressource, puis sa pièce jointe, dans la même transaction. Les octets
     * restent sur l'appareil jusqu'à leur envoi, qui part après la synchronisation des deux
     * lignes, et reprend au lancement suivant s'il a été coupé.
     */
    @OptIn(ExperimentalUuidApi::class)
    suspend fun addFile(skillId: String, title: String, file: PickedFile): Added = withContext(Dispatchers.Default) {
        ResourceRules.fileProblem(file.name, file.bytes)?.let { return@withContext Added.Problem(it) }
        ResourceRules.titleProblem(title)?.let { return@withContext Added.Problem(it) }
        quotaProblem(skillId)?.let { return@withContext Added.Problem(it) }
        val kind = ResourceRules.kindOf(file.name) ?: return@withContext Added.Problem("Ce type de fichier n'est pas accepté.")
        val sha = ResourceRules.hex(Pbkdf2.sha256(file.bytes))
        if (sha in db.attachmentQueries.selectResourceShas(skillId).executeAsList()) {
            return@withContext Added.Problem("Ce fichier est déjà dans la compétence.")
        }
        val now = nowMillis()
        val fileId = Uuid.random().toString()
        var id = ""
        db.transaction {
            id = insert(skillId, "file", kind.format, title.trim(), null, null, now)
            db.fileBlobQueries.put(fileId, file.bytes, now)
            db.attachmentQueries.insert(
                fileId, OWNER, id, file.name.substringAfterLast('/').substringAfterLast('\\'),
                ResourceRules.mimeOf(kind, file.name), file.bytes.size.toLong(), null, sha,
                null, null, "pending", 0L, now, now,
            )
        }
        Added.Done(id)
    }

    suspend fun rename(id: String, title: String): String? = withContext(Dispatchers.Default) {
        ResourceRules.titleProblem(title)?.let { return@withContext it }
        db.skillResourceQueries.rename(title.trim(), nowMillis(), id)
        null
    }

    /** Échange la place de deux ressources voisines. */
    suspend fun swap(a: Skill_resource, b: Skill_resource) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        // Deux rangs égaux ne s'échangent pas : on les écarte d'abord.
        val (ra, rb) = if (a.order_index == b.order_index) a.order_index to a.order_index + 1 else a.order_index to b.order_index
        db.transaction {
            db.skillResourceQueries.setOrder(rb, now, a.id)
            db.skillResourceQueries.setOrder(ra, now, b.id)
        }
    }

    /** Supprime : le serveur fait suivre le fichier, et efface l'analyse. */
    suspend fun delete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.skillResourceQueries.softDelete(now, now, id)
    }

    /** Sort de la corbeille. Le serveur revérifie le quota et les doublons. */
    suspend fun restore(id: String) = withContext(Dispatchers.Default) {
        db.skillResourceQueries.restoreFromTrash(nowMillis(), id)
    }

    private fun quotaProblem(skillId: String): String? =
        if (db.skillResourceQueries.countLive(skillId).executeAsOne() >= ResourceRules.MAX_PER_SKILL) {
            "La compétence a déjà ${ResourceRules.MAX_PER_SKILL} ressources. Supprime-en une pour en ajouter."
        } else null

    @OptIn(ExperimentalUuidApi::class)
    private fun insert(skillId: String, origin: String, format: String, title: String, url: String?, body: String?, now: Long = nowMillis()): String {
        val id = Uuid.random().toString()
        val next = db.skillResourceQueries.maxOrder(skillId).executeAsOne() + GAP
        db.skillResourceQueries.insert(id, skillId, null, origin, format, title, url, body, next, now, now)
        return id
    }

    companion object {
        const val OWNER = "skill_resource"
        private const val GAP = 1024L
        private const val TRASH_MS = 30L * 24 * 3600 * 1000
    }
}
